import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { money } from "../../lib/api";
import { QuickAddDialog } from "./QuickAddDialog";
import { FavoriteButton } from "./FavoriteButton";

export const DraftBadge = ({ className = "" }) => (
  <span className={`inline-flex items-center rounded-full bg-sunset text-white text-[10px] font-extrabold tracking-widest px-2.5 py-1 ${className}`} data-testid="draft-badge">
    DRAFT · NOT FOR SALE
  </span>
);

export const SoldOutBadge = ({ className = "" }) => (
  <span className={`inline-flex items-center rounded-full bg-navy text-white text-[10px] font-extrabold tracking-widest px-2.5 py-1 ${className}`} data-testid="sold-out-badge">
    SOLD OUT
  </span>
);

export const ProductCard = ({ p }) => {
  const [quick, setQuick] = useState(false);
  const [shade, setShade] = useState(null);
  const thumb = (shade && p.images_by_color?.[shade]?.[0]) || p.images?.[0];
  const draft = p.status !== "active";
  const sizes = p.sizes?.length > 1 ? `${p.sizes[0]}–${p.sizes[p.sizes.length - 1]}` : p.sizes?.[0];
  return (
    <div className="group" data-testid={`product-card-${p.slug}`}>
      <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-sand">
        <Link to={`/products/${p.slug}`} data-testid={`product-card-link-${p.slug}`}>
          <img src={thumb} alt={`${p.name} – ${p.product_type} vacation apparel`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </Link>
        {draft && <DraftBadge className="absolute top-3 left-3" />}
        {!draft && p.sold_out && <SoldOutBadge className="absolute top-3 left-3" />}
        <FavoriteButton slug={p.slug} className="absolute top-3 right-3" />
        {!draft && !p.sold_out && (
          <button
            onClick={() => setQuick(true)}
            className="absolute bottom-3 right-3 h-11 rounded-full bg-navy text-white flex items-center gap-1.5 px-4 text-sm font-bold shadow-lg sm:opacity-0 sm:translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform]"
            data-testid={`quick-add-${p.slug}`}
          >
            <Plus className="w-4 h-4" /> Quick Add
          </button>
        )}
      </div>
      <div className="pt-4 px-1">
        <div className="flex items-start justify-between gap-3">
          <Link to={`/products/${p.slug}`} className="font-semibold text-navy leading-snug hover:text-[#0078a0] transition-colors">
            {p.name}
          </Link>
          <span className="font-bold text-navy shrink-0" data-testid={`product-price-${p.slug}`}>{money(p.price)}</span>
        </div>
        <div className="mt-2 flex items-center gap-3 text-xs text-navy/60">
          <span className="flex -space-x-1" aria-label="Available colors">
            {p.colors?.slice(0, 8).map((c) => (
              <button key={c.name} type="button" title={c.name} aria-label={`Show ${c.name}`} onMouseEnter={() => setShade(c.name)} onClick={() => setShade(c.name)}
                className={`w-4 h-4 rounded-full ring-2 border border-navy/15 transition-transform hover:scale-125 ${shade === c.name ? "ring-turq" : "ring-white"}`} style={{ background: c.hex }} data-testid={`card-swatch-${p.slug}-${c.name.toLowerCase().replace(/\s+/g, "-")}`} />
            ))}
          </span>
          <span>{p.colors?.length} colors · {sizes}</span>
        </div>
      </div>
      {!draft && !p.sold_out && <QuickAddDialog p={p} open={quick} onOpenChange={setQuick} />}
    </div>
  );
};
